var moment = require('moment-timezone');

export const offsetDateForBrowser = (date, venueTimeZone = null) => {
  if(!date){ return date; }

  // SSR-safe: use provided timezone or fallback to gon if available
  const timeZone = venueTimeZone || (typeof gon !== 'undefined' ? gon.timeZone : 'America/New_York');

  // SSR-safe: get browser timezone or fallback
  const browserTZ = typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'America/New_York';

  // The date string should be passed from the server
  // in the correct time_zone for the VenueOwnership.
  // Moment will parse this date string using the timeZone
  // specified and produce a Date object in the local timeZone.
  //
  // Example:
  //   date = 2022-11-27T22:45:00.833-05:00
  //   gon.timeZone = America/New_York
  //   browserTZ = Pacific/Honolulu
  //   original = Sun Nov 27 2022 17:45:00 GMT-1000 (Hawaii-Aleutian Standard Time)
  //
  // The original time in EST was 10:45 PM, so when parsed by Moment
  // it is adjusted to the local time as 5:45 PM Hawaiian time.
  // Even though the user is in Hawaii, we want them to see the original
  // time of 10:45 PM.
  var original = moment.tz(date, timeZone);

  // Now that we have the time in the local time_zone, we need
  // to offset the time to compensate for the time_zone difference.
  // Moment timezone will do this work for us by passing "true"
  // as the second argument while we convert to the local time_zone.
  //
  // Example:
  //   date = 2022-11-27T22:45:00.833-05:00
  //   gon.timeZone = America/New_York
  //   browserTZ = Pacific/Honolulu
  //   original = Sun Nov 27 2022 17:45:00 GMT-1000 (Hawaii-Aleutian Standard Time)
  //   adjusted = Sun Nov 27 2022 22:45:00 GMT-1000 (Hawaii-Aleutian Standard Time)
  //
  // The time is offset by 5 hours to be 10:45 PM Hawaiin time so that we
  // can display the desired value to the user. Under the hood, this new
  // time is actually 3:45 AM (the next day) in EST time. We will have to
  // adjust a second time when we send the value back to the server.
  var adjusted = original.clone().tz(browserTZ, true);

  return adjusted.toDate();
}

// A datetime string carrying an explicit offset (or "Z") is an absolute instant
// rendered by the server -- a venue-local wall clock, not a browser-local one.
// The relabel below assumes browser-local, so handing it one of these shifts the
// value by (browserOffset - venueOffset) on every save, compounding each time.
// Naive strings ("2026-08-07 09:55") carry no such claim and are left alone.
//
// The offset must follow a time of day. A trailing sign-and-digits is not enough:
// "2026-08-07" ends in "-07" and a US-ordered "08-07-2026" ends in "-2026",
// neither of which is an offset.
const OFFSET_AFTER_TIME_OF_DAY = /\d{2}:\d{2}(:\d{2})?(\.\d+)?\s*(Z|[+-]\d{2}:?\d{2})$/i;

// Every shape Rails renders, listed explicitly so moment never falls back to
// `new Date(string)`. `Time#to_s` ("2026-08-07 09:55:00 -0400") is not ISO, and
// the fallback parser reads it in V8 but returns Invalid Date in Safari -- which
// would trade a predictable offset bug for a NaN on one browser.
const SERVER_DATETIME_FORMATS = [
  moment.ISO_8601,
  'YYYY-MM-DD HH:mm:ss Z',
  'YYYY-MM-DD HH:mm Z',
];

// Opposite of offsetDateForBrowser
export const offsetDateForServer = (date, venueTimeZone = null) => {
  if(!date){ return date; }

  if(typeof date === 'string' && OFFSET_AFTER_TIME_OF_DAY.test(date)){
    // parseZone keeps the offset the string declares, so the instant is read
    // correctly no matter which zone the browser is in.
    date = offsetDateForBrowser(
      moment.parseZone(date, SERVER_DATETIME_FORMATS).toDate(),
      venueTimeZone
    );
  }

  const timeZone = venueTimeZone || (typeof gon !== 'undefined' ? gon.timeZone : 'America/New_York');
  const browserTZ = typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'America/New_York';

  var original = moment.tz(date, browserTZ);
  var adjusted = original.clone().tz(timeZone, true);

  return adjusted.toDate();
}

// For date-only columns. A date has no instant to convert, so offsetting it and
// letting Rails cast the UTC prefix rolls it: an evening show at a venue west of
// UTC serializes past midnight and lands a day late, in every browser zone. Take
// the calendar date off the wall clock the user picked instead.
export const toCalendarDate = (date) => {
  if(!date){ return date; }

  return moment(date).format('YYYY-MM-DD');
}

// On some views (that involve multiple Venues), we can't set a single
// timeZone for the page since it can change as the Venue changes.
// In an ideal world the user would be picking from VenueOwnerships
// instead of Venues and we wouldn't need this...
export const setGonTimeZoneFromVenue = (venueOwnerships, venueId) => {
  var currentVenueOwnership = venueOwnerships.find((vo) =>
    vo.venueable_type === "Venue"
      && vo.venueable_id === venueId
  );

  var timeZone;
  if(currentVenueOwnership && currentVenueOwnership.tzinfo_identifier){
    timeZone = currentVenueOwnership.tzinfo_identifier;
  } else {
    timeZone = "America/New_York";
  }

  gon.timeZone = timeZone;
  return timeZone;
}

const getBrowserTimeZone = () =>
  typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'America/New_York';

export const offsetDateToTimezone = (date, timeZone) => {
  var original = moment.tz(date, timeZone);
  var adjusted = original.clone().tz(getBrowserTimeZone(), true);
  return adjusted.toDate();
}

export const offsetDateFromTimeZone = (date, timeZone) => {
  var original = moment.tz(date, getBrowserTimeZone());
  var adjusted = original.clone().tz(timeZone, true);
  return adjusted.toDate();
}

export const utcToTimeZone = (utcDate, timeZone) => {
  if (!utcDate) return utcDate;
  
  var utcMoment = moment.utc(utcDate);
  var targetMoment = utcMoment.clone().tz(timeZone);
  
  var year = targetMoment.year();
  var month = targetMoment.month();
  var date = targetMoment.date();
  var hours = targetMoment.hours();
  var minutes = targetMoment.minutes();
  var seconds = targetMoment.seconds();
  var milliseconds = targetMoment.milliseconds();
  
  return new Date(year, month, date, hours, minutes, seconds, milliseconds);
}

